// Per-location color coding (2026-08 Green PT pilot; 2026-02 expansion).
// ONE deterministic palette shared by the Month view and the weekly
// Calendar: the user's locations sorted stably, assigned from a fixed
// accessible palette of 12 distinguishable hues. Same location = same
// color everywhere, every session. >12 locations wrap with a numbered
// suffix on the label — color is never the only signal.
//
// PALETTE CONTRACT (pinned by tests):
//   Positions 1–6 are BYTE-IDENTICAL to the Green PT pilot (blue,
//   violet, teal, rose, lime, fuchsia). Green PT's scheduler has been
//   using these colors daily since Aug 2026 — they must not shift.
//   Positions 7+ MAY change; indigo is RESERVED for direct bookings on
//   both calendars and must NOT appear in this location palette.
//
// STATUS-COLOR RESERVATION (never used for a location hue):
//   amber   → Pending
//   orange  → Alt Suggested (Alt Sent)
//   emerald → Confirmed / locked_in
//   indigo  → Direct booking (`ClinicScheduling.jsx` `legend-direct`,
//             `ClinicMonthView.jsx` direct-booking chips,
//             `NetworkCalendarGrid.jsx` `Direct` label)
//
// ACCESSIBILITY:
//   Verified with Machado 2009 dichromacy simulation matrices at full
//   severity (deuteranopia, protanopia). New positions 7–12 have a
//   pairwise min distance of 22 (deut) / 15 (prot) — pastel backgrounds
//   converge somewhat, but chip labels are ALWAYS rendered so color is
//   never the sole channel. Pre-existing pilot pairs (blue↔violet in
//   deut, violet↔fuchsia in prot) are inherited unchanged from the
//   pinned positions 1–6. Swatch/stripe values use the -500/-600 shade
//   which is where users perceptually key on the color; -100 chip
//   backgrounds are inherently light and paired with strong labels.

export const LOCATION_PALETTE = [
  // Positions 1–6 — Green PT pilot pins. Byte-identical.
  { name: "blue",    chip: "bg-blue-100 text-blue-900 border-blue-400",         swatch: "#3b82f6", stripe: "#3b82f6" },
  { name: "violet",  chip: "bg-violet-100 text-violet-900 border-violet-400",   swatch: "#8b5cf6", stripe: "#8b5cf6" },
  { name: "teal",    chip: "bg-teal-100 text-teal-900 border-teal-400",         swatch: "#14b8a6", stripe: "#14b8a6" },
  { name: "rose",    chip: "bg-rose-100 text-rose-900 border-rose-400",         swatch: "#f43f5e", stripe: "#f43f5e" },
  { name: "lime",    chip: "bg-lime-100 text-lime-900 border-lime-500",         swatch: "#84cc16", stripe: "#84cc16" },
  { name: "fuchsia", chip: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-400", swatch: "#d946ef", stripe: "#d946ef" },
  // Position 7 unchanged from prior 8-hue palette.
  { name: "cyan",    chip: "bg-cyan-100 text-cyan-900 border-cyan-400",         swatch: "#06b6d4", stripe: "#06b6d4" },
  // Positions 8–12 — 2026-02 expansion. Warm tones prioritized per
  // ticket; indigo REMOVED (now direct-booking-only). Swatch/stripe use
  // -600 shades for the new hues so their saturation stays above the
  // pastel pool and colorblind separation is preserved.
  { name: "red",     chip: "bg-red-100 text-red-900 border-red-400",            swatch: "#dc2626", stripe: "#dc2626" },
  { name: "yellow",  chip: "bg-yellow-100 text-yellow-900 border-yellow-500",   swatch: "#ca8a04", stripe: "#ca8a04" },
  { name: "sky",     chip: "bg-sky-100 text-sky-900 border-sky-400",            swatch: "#0284c7", stripe: "#0284c7" },
  { name: "pink",    chip: "bg-pink-100 text-pink-900 border-pink-400",         swatch: "#db2777", stripe: "#db2777" },
  { name: "stone",   chip: "bg-stone-100 text-stone-900 border-stone-400",      swatch: "#78716c", stripe: "#78716c" },
];

// codes: array of location_code strings. Returns { [code]: { color, suffix } }.
// Stable: input order never matters (sorted copy), palette index = sorted rank.
export function assignLocationColors(codes) {
  const sorted = [...new Set(codes || [])].sort();
  const map = {};
  sorted.forEach((code, i) => {
    map[code] = {
      color: LOCATION_PALETTE[i % LOCATION_PALETTE.length],
      // Wrap disambiguation: 13th+ locations share a hue, so the LABEL
      // carries a numbered suffix — color is never the only signal.
      suffix: i >= LOCATION_PALETTE.length ? ` (${Math.floor(i / LOCATION_PALETTE.length) + 1})` : "",
    };
  });
  return map;
}

// Short human label for a location — city-first ("Menifee", "Hemet").
// profileLocations: the /clinic/me `locations` array.
export function locationLabelMap(profileLocations) {
  const map = {};
  (profileLocations || []).forEach((l) => {
    map[l.location_code] = l.short_label || l.location_name || l.location_code;
  });
  return map;
}
